import React, { useState, useEffect, useRef } from "react"
import Popup from "../../component/popup/popup"
import InputText from "../../component/inputText/inputText"
import Select from "../../component/select/select"
import Grid from "@mui/material/Grid"
import IconsColor from "../../util/iconsColor"
import { PlaceData } from "../../models"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"
import { getActiveCityOptions } from "../../util/cityOptionsUtil"
import styles from "../../asset/styles/places/places.module.scss"

export type PlacePopupMode = "add" | "update" | "status" | "delete"

type PlacesPopupProps = {
    isOpen: boolean
    onClose: () => void
    mode: PlacePopupMode
    place?: PlaceData | null
    placeUsageData?: { day_trip_name: string }[]
    onSuccess?: () => void
    onConfirmStatus?: () => void
    onConfirmDelete?: () => void
}

const PlacesPopup: React.FC<PlacesPopupProps> = ({
    isOpen,
    onClose,
    mode,
    place,
    placeUsageData = [],
    onSuccess,
    onConfirmStatus,
    onConfirmDelete,
}) => {
    const { hotelGenericData } = useHotelGeneric()
    const { showError, showSuccess } = useSnackbar()

    const [placeName, setPlaceName] = useState("")
    const [cityId, setCityId] = useState("")
    const [placeTypeId, setPlaceTypeId] = useState("")
    const [errors, setErrors] = useState<{ placeName?: string; cityId?: string; placeTypeId?: string }>({})
    const [placeCheckWarning, setPlaceCheckWarning] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const checkPlaceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const isFormMode = mode === "add" || mode === "update"
    const isConfirmMode = mode === "status" || mode === "delete"

    const title =
        mode === "add"
            ? "Add Place"
            : mode === "update"
              ? "Update Place"
              : mode === "status"
                ? "Change Place Status"
                : "Delete Place"

    useEffect(() => {
        if (isOpen && isFormMode) {
            if (mode === "update" && place) {
                setPlaceName(place.place_name ?? "")
                setCityId(String(place.city_id ?? ""))
                setPlaceTypeId(String(place.place_type_id ?? ""))
            } else {
                setPlaceName("")
                setCityId("")
                setPlaceTypeId("")
            }
            setErrors({})
            setPlaceCheckWarning(null)
        }
    }, [isOpen, mode, place, isFormMode])

    useEffect(() => {
        if (!isOpen || !isFormMode) return
        const name = placeName?.trim()
        const city = cityId?.trim()
        if (!name || !city) {
            setPlaceCheckWarning(null)
            return
        }
        if (checkPlaceTimeoutRef.current) {
            clearTimeout(checkPlaceTimeoutRef.current)
            checkPlaceTimeoutRef.current = null
        }
        checkPlaceTimeoutRef.current = setTimeout(async () => {
            checkPlaceTimeoutRef.current = null
            try {
                const response = await agent.CheckPlaceAPI.post(
                    { place_name: name, city_id: Number(city), place_id: place?.place_id ?? null },
                    true
                )
                if (response?.status === true && Array.isArray(response?.data) && response.data.length > 0) {
                    const messages = response.data
                        .map((item: { message?: string }) => item?.message)
                        .filter(Boolean)
                    setPlaceCheckWarning(messages.length > 0 ? messages.join(" ") : null)
                } else {
                    setPlaceCheckWarning(null)
                }
            } catch {
                setPlaceCheckWarning(null)
            }
        }, 400)
        return () => {
            if (checkPlaceTimeoutRef.current) {
                clearTimeout(checkPlaceTimeoutRef.current)
            }
        }
    }, [isOpen, isFormMode, placeName, cityId, place?.place_id])

    const validate = (): boolean => {
        const next: { placeName?: string; cityId?: string; placeTypeId?: string } = {}
        if (!placeName?.trim()) next.placeName = "Place Name is required"
        if (!cityId?.trim()) next.cityId = "City is required"
        if (!placeTypeId?.trim()) next.placeTypeId = "Place Type is required"
        setErrors(next)
        return Object.keys(next).length === 0
    }

    const buildPutBody = (): Record<string, unknown> => {
        if (!place) return {}
        const cityName =
            (hotelGenericData?.city ?? []).find((c: { city_id: number }) => String(c.city_id) === cityId)?.city_name ??
            place.city_name
        const placeTypeName =
            (hotelGenericData?.place_type ?? []).find(
                (p: { place_type_id: number }) => String(p.place_type_id) === placeTypeId
            )?.place_type_name ?? place.place_type_name
        return {
            place_id: place.place_id,
            place_name: placeName.trim(),
            city_id: Number(cityId) || place.city_id,
            city_name: cityName,
            place_type_id: Number(placeTypeId) || place.place_type_id,
            place_type_name: placeTypeName,
            flag_active: place.flag_active,
        }
    }

    const handleSave = async () => {
        if (!validate()) return
        if (mode === "update" && !place?.place_id) return
        setIsSaving(true)
        setErrors({})
        try {
            if (mode === "add") {
                const response = await agent.PlaceListAPI.put({
                    place_name: placeName.trim(),
                    city_id: Number(cityId),
                    place_type_id: Number(placeTypeId),
                    flag_active: true,
                    flag_delete: false,
                    place_id: null,
                    city_name: null,
                    place_type_name: null,
                })
                if (response?.status === true) {
                    showSuccess("Place added successfully")
                    onSuccess?.()
                    onClose()
                } else {
                    showError(response?.error?.message ?? "Failed to add place")
                }
            } else {
                const body = buildPutBody()
                const response = await agent.PlaceListAPI.put(body as any)
                if (response?.status === true) {
                    showSuccess("Place updated successfully")
                    onSuccess?.()
                    onClose()
                } else {
                    showError(response?.error?.message ?? "Failed to update place")
                }
            }
        } catch (err) {
            console.error("Place save error", err)
            showError(mode === "add" ? "Failed to add place" : "Failed to update place")
        } finally {
            setIsSaving(false)
        }
    }

    const handleConfirm = () => {
        if (mode === "status") {
            onClose()
            onConfirmStatus?.()
        } else if (mode === "delete") {
            onClose()
            onConfirmDelete?.()
        }
    }

    const cityOptions = getActiveCityOptions(hotelGenericData?.city, cityId)
    const placeTypeOptions = (hotelGenericData?.place_type ?? []).map(
        (x: { place_type_id: number; place_type_name: string }) => ({
            label: x.place_type_name,
            value: String(x.place_type_id),
        })
    )

    if (isConfirmMode) {
        if (!place) return null
        return (
            <Popup
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                primaryButton={{ label: "Confirm", onClick: handleConfirm }}
                secondaryButton={{ label: "Cancel", onClick: onClose }}
                heightFiexd={true}
            >
                {mode === "status" ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{place.place_name}</strong> to{" "}
                            <strong>{place.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                ) : (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to delete <strong>{place.place_name}</strong>?
                        </p>
                        {placeUsageData.length > 0 ? (
                            <div className={styles.placeUsageWarning}>
                                <p className={styles.placeUsageWarningTitle}>
                                    The day trips listed below and their related packages will become inactive.
                                </p>
                                <ul className={styles.placeUsageList}>
                                    {placeUsageData.map((item, index) => (
                                        <li key={index}>{item.day_trip_name}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                )}
            </Popup>
        )
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            primaryButton={{ label: isSaving ? "Saving..." : "Save", onClick: handleSave, disabled: isSaving }}
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            heightFiexd={true}
        >
            <Grid container spacing={2} style={{ paddingTop: 8 }}>
                <Grid size={{ xs: 12 }}>
                    <InputText
                        warning={placeCheckWarning ?? undefined}
                        label="Place Name"
                        value={placeName}
                        onChange={(e) => setPlaceName(e.target.value)}
                        placeholder="Enter Place Name"
                        required
                        error={errors.placeName}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                        label="City"
                        value={cityId}
                        onChange={(e) => setCityId(e.target.value)}
                        options={cityOptions}
                        placeholder="Select City"
                        required
                        error={errors.cityId}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                        label="Place Type"
                        value={placeTypeId}
                        onChange={(e) => setPlaceTypeId(e.target.value)}
                        options={placeTypeOptions}
                        placeholder="Select Place Type"
                        required
                        error={errors.placeTypeId}
                    />
                </Grid>
            </Grid>
        </Popup>
    )
}

export default PlacesPopup
